import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { AlertTriangle, ArrowLeft, CheckCircle2, FileText, ShieldCheck } from 'lucide-react';
import SanspathLogo from '../../components/auth/SanspathLogo';
import { legalAPI } from '../../services/api';

const pageConfig = {
  terms: {
    label: 'Terms of Service',
    fallbackTitle: 'Employer Terms of Service',
    description: 'The terms that govern employer use of Sanspath.'
  },
  privacy: {
    label: 'Privacy Policy',
    fallbackTitle: 'Employer Privacy Policy',
    description: 'How employer account and company information is handled on Sanspath.'
  }
};

const formatDate = (value) => {
  if (!value) return 'Not published';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not published';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(date);
};

const getDocumentFromResponse = (response) => (
  response?.document ||
  response?.data?.document ||
  response?.legalDocument ||
  response?.data?.legalDocument ||
  null
);

const splitContent = (content) => String(content || '')
  .split(/\n{2,}/)
  .map((part) => part.trim())
  .filter(Boolean);

const renderContentBlock = (block, index) => {
  const normalized = block.replace(/^#{1,3}\s*/, '').trim();
  const lines = normalized.split('\n').map((line) => line.trim()).filter(Boolean);
  const bulletLines = lines.filter((line) => /^[-*]\s+/.test(line));
  const isHeading = lines.length === 1 && normalized.length <= 96 && (/^[0-9]+[.)]\s+/.test(normalized) || /:$/.test(normalized));

  if (isHeading) {
    return (
      <h2 key={`${index}-${normalized.slice(0, 18)}`} className="pt-2 text-lg font-semibold tracking-tight text-slate-950">
        {normalized.replace(/:$/, '')}
      </h2>
    );
  }

  if (bulletLines.length === lines.length) {
    return (
      <ul key={`${index}-${normalized.slice(0, 18)}`} className="space-y-3 text-sm leading-7 text-slate-700">
        {lines.map((line) => (
          <li key={line} className="flex gap-3">
            <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-sky-600" />
            <span className="min-w-0 break-words">{line.replace(/^[-*]\s+/, '')}</span>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <p key={`${index}-${normalized.slice(0, 18)}`} className="whitespace-pre-wrap break-words text-sm leading-7 text-slate-700">
      {normalized}
    </p>
  );
};

function LegalPage({ documentType = 'terms' }) {
  const config = pageConfig[documentType] || pageConfig.terms;
  const query = useQuery(['employer-legal-page', documentType], () => legalAPI.getPage(documentType), {
    retry: 1,
    staleTime: 5 * 60 * 1000
  });
  const document = getDocumentFromResponse(query.data);
  const paragraphs = useMemo(() => splitContent(document?.content), [document?.content]);
  const isPrivacyPage = documentType === 'privacy';

  return (
    <main className="min-h-screen bg-[#f8fafc] text-slate-950">
      <div className="mx-auto flex min-h-screen w-full max-w-5xl flex-col px-5 py-8 sm:px-8 lg:px-10">
        <header className="flex flex-col gap-5 border-b border-slate-200 pb-6 sm:flex-row sm:items-center sm:justify-between">
          <SanspathLogo imageClassName="h-10" />
          <div className="flex items-center gap-2">
            <Link to="/login" className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
              Sign in
            </Link>
            <Link to="/register" className="inline-flex h-10 items-center justify-center rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white transition hover:bg-slate-800">
              Create account
            </Link>
          </div>
        </header>

        <section className="py-10 sm:py-14">
          <Link to="/register" className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition hover:text-slate-950">
            <ArrowLeft size={16} />
            Back to registration
          </Link>

          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_18rem]">
            <article className="min-w-0">
              <div className="mb-8 flex items-start gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700">
                  {isPrivacyPage ? <ShieldCheck size={22} /> : <FileText size={22} />}
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Sanspath employer</p>
                  <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                    {document?.title || config.fallbackTitle}
                  </h1>
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
                    {document?.summary || config.description}
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
                {query.isLoading ? (
                  <div className="space-y-4">
                    {[0, 1, 2, 3, 4].map((item) => <div key={item} className="h-4 animate-pulse rounded bg-slate-200" />)}
                  </div>
                ) : query.isError ? (
                  <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm leading-6 text-rose-700">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <p>This page could not be loaded right now. Please try again later.</p>
                  </div>
                ) : paragraphs.length ? (
                  <div className="space-y-5">
                    {paragraphs.map(renderContentBlock)}
                  </div>
                ) : (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-800">
                    This legal page has not been published yet.
                  </div>
                )}
              </div>
            </article>

            <aside className="h-fit rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Page details</p>
              <div className="mt-4 grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-1">
                <Link to="/terms" className={`rounded-md px-3 py-2 text-center text-xs font-semibold transition ${!isPrivacyPage ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>
                  Terms
                </Link>
                <Link to="/privacy" className={`rounded-md px-3 py-2 text-center text-xs font-semibold transition ${isPrivacyPage ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>
                  Privacy
                </Link>
              </div>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between gap-4 border-b border-slate-100 pb-3">
                  <dt className="text-slate-500">Audience</dt>
                  <dd className="font-semibold text-slate-900">Employer</dd>
                </div>
                <div className="flex justify-between gap-4 border-b border-slate-100 pb-3">
                  <dt className="text-slate-500">Type</dt>
                  <dd className="font-semibold text-slate-900">{config.label}</dd>
                </div>
                <div className="flex justify-between gap-4 border-b border-slate-100 pb-3">
                  <dt className="text-slate-500">Version</dt>
                  <dd className="font-semibold text-slate-900">{document?.version || '-'}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500">Published</dt>
                  <dd className="text-right font-semibold text-slate-900">{formatDate(document?.publishedAt)}</dd>
                </div>
              </dl>
            </aside>
          </div>
        </section>
      </div>
    </main>
  );
}

export default LegalPage;
